/**
 * useFileDropUpload - Uploads dropped files and converts to QueueImage format
 *
 * Takes raw File objects, uploads via uploadPendingImage API,
 * and returns QueueImage[] for use with ProductImagesSubTab.
 */

import { useState, useCallback } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { uploadPendingImage } from '@/lib/api/products'
import type { QueueImage } from '@/lib/api/image-queue'

interface FileDropUploadState {
  productId: string | null
  productName: string
  uploading: boolean
  uploadedImages: QueueImage[]
}

export function useFileDropUpload() {
  const { t } = useTranslation()
  const [state, setState] = useState<FileDropUploadState>({
    productId: null,
    productName: '',
    uploading: false,
    uploadedImages: [],
  })

  const uploadFiles = useCallback(
    async (productId: string, productName: string, files: File[]) => {
      setState({
        productId,
        productName,
        uploading: true,
        uploadedImages: [],
      })

      const uploaded: QueueImage[] = []
      const now = new Date().toISOString()

      for (const file of files) {
        try {
          const result = await uploadPendingImage(productId, file)
          if (result.success) {
            uploaded.push({
              id: result.asset_id,
              run_id: 'file-drop',
              asset_id: result.asset_id,
              output_url: URL.createObjectURL(file),
              s3_key: undefined,
              width: result.width,
              height: result.height,
              status: 'completed',
              created_at: now,
            })
          } else {
            toast.error(t('errors.uploadFailedFile', { fileName: file.name }))
          }
        } catch {
          toast.error(t('errors.uploadFailedFile', { fileName: file.name }))
        }
      }

      setState((prev) => ({
        ...prev,
        uploading: false,
        uploadedImages: uploaded,
      }))

      if (uploaded.length > 0) {
        toast.success(
          `${uploaded.length} Bild${uploaded.length > 1 ? 'er' : ''} hochgeladen`
        )
      }
    },
    [t]
  )

  const reset = useCallback(() => {
    setState((prev) => {
      prev.uploadedImages.forEach((img) => {
        if (img.output_url?.startsWith('blob:')) {
          URL.revokeObjectURL(img.output_url)
        }
      })
      return {
        productId: null,
        productName: '',
        uploading: false,
        uploadedImages: [],
      }
    })
  }, [])

  return { ...state, uploadFiles, reset }
}
